<script setup>
import { ref, computed } from "vue";

const props = defineProps({
	modelValue: {
		type: Array,
		default: () => [],
	},
	acceptedTypes: {
		type: Array,
		default: () => [
			"image/jpeg",
			"image/png",
			"application/pdf",
			"application/msword",
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		],
	},
	acceptedExtensions: {
		type: Array,
		default: () => [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx"],
	},
	label: {
		type: String,
		default: "Click to add files or drag and drop",
	},
	maxFiles: {
		type: Number,
		default: null,
	},
	maxSize: {
		type: Number,
		default: null, // in bytes
	},
	error: {
		type: String,
		default: null,
	},
});

const emit = defineEmits(["update:modelValue"]);

const fileInput = ref(null);
const files = ref([]);

const hasError = computed(() => !!props.error);
const errorMessage = computed(() => props.error);

const handleFileSelect = (event) => {
	const selectedFiles = Array.from(event.target.files);
	const validFiles = [];

	for (const file of selectedFiles) {
		// Check file type
		if (!props.acceptedTypes.includes(file.type)) {
			alert(`File type not allowed: ${file.name}`);
			continue;
		}

		// Check file size
		if (props.maxSize && file.size > props.maxSize) {
			alert(
				`File "${file.name}" is too large. Maximum size is ${formatFileSize(props.maxSize)}`,
			);
			continue;
		}

		// Check max files
		if (
			props.maxFiles &&
			files.value.length + validFiles.length >= props.maxFiles
		) {
			alert(`Maximum ${props.maxFiles} files allowed`);
			break;
		}

		validFiles.push({
			file,
			name: file.name,
			preview: file.type.startsWith("image/")
				? URL.createObjectURL(file)
				: getFileIcon(file.type),
		});
	}

	if (props.maxFiles > 1) {
		files.value = [...files.value, ...validFiles];
	} else {
		files.value = validFiles.slice(0, 1);
	}

	updateModelValue();

	// Reset input
	event.target.value = "";
};

const removeFile = (index) => {
	// Revoke object URL to prevent memory leaks
	if (files.value[index].preview.startsWith("blob:")) {
		URL.revokeObjectURL(files.value[index].preview);
	}

	files.value.splice(index, 1);
	updateModelValue();
};

const downloadFile = (index) => {
	const file = files.value[index]?.file;
	if (!file) return;

	const url = URL.createObjectURL(file);

	const a = document.createElement("a");
	a.href = url;
	a.download = file.name;
	document.body.appendChild(a);
	a.click();
	a.remove();

	// revoke shortly after click so the download can start
	setTimeout(() => URL.revokeObjectURL(url), 500);
};

const updateModelValue = () => {
	emit(
		"update:modelValue",
		files.value.map((f) => f.file),
	);
};

const getFileIcon = (type) => {
	if (type === "application/pdf") return "/dist/bolster/img/pdf.png";
	if (type.includes("word")) return "/dist/bolster/img/word.png";
	return "";
};

const formatFileSize = (bytes) => {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const triggerFileInput = () => {
	fileInput.value?.click();
};

// Initialize with existing files if provided
if (props.modelValue.length > 0) {
	files.value = props.modelValue.map((file) => ({
		file,
		name: file.name,
		preview: file.type.startsWith("image/")
			? URL.createObjectURL(file)
			: getFileIcon(file.type),
	}));
}
</script>

<template>
	<div class="file-uploader">
		<input
			ref="fileInput"
			type="file"
			:multiple="maxFiles > 1"
			:accept="acceptedExtensions.join(',')"
			@change="handleFileSelect"
			style="display: none"
		/>

		<div
			class="file-upload-area"
			@click="triggerFileInput"
			:class="{ 'has-files': files.length > 0, 'is-invalid': hasError }"
		>
			<div v-if="files.length === 0" class="upload-prompt">
				<i class="ri-upload-cloud-line"></i>
				<p>{{ label }}</p>
				<small>Accepted: {{ acceptedExtensions.join(", ") }}</small>
			</div>

			<div v-else class="uploaded-files">
				<div v-for="(file, index) in files" :key="index" class="uploaded-file">
					<img
						v-if="file.file.type.startsWith('image/')"
						:src="file.preview"
						alt="Preview"
					/>
					<img v-else :src="file.preview" alt="File icon" />
					<span>{{ file.name }}</span>
					<button
						type="button"
						@click.stop="removeFile(index)"
						class="remove-file"
					>
						<i class="ri-close-line"></i>
					</button>

					<!-- Download button -->
					<button
						type="button"
						@click.stop="downloadFile(index)"
						class="download-file"
						aria-label="Download file"
						title="Download"
					>
						<i class="ri-download-2-line"></i>
					</button>
				</div>
			</div>
		</div>

		<div v-if="errorMessage" class="invalid-feedback">
			{{ errorMessage }}
		</div>
	</div>
</template>

<style scoped>
.file-upload-area {
	border: 2px dashed #ccc;
	border-radius: 8px;
	padding: 20px;
	text-align: center;
	cursor: pointer;
	transition: border-color 0.3s;
}

.file-upload-area:hover {
	border-color: #007bff;
}

.file-upload-area.has-files {
	border-style: solid;
	border-color: #28a745;
}

.file-upload-area.is-invalid {
	border-color: #dc3545;
}

.upload-prompt i {
	font-size: 2rem;
	color: #6c757d;
	margin-bottom: 10px;
}

.uploaded-files {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	justify-content: center;
}

.uploaded-file {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 10px;
	border: 1px solid #ddd;
	border-radius: 8px;
	background: #f8f9fa;
	max-width: 120px;
}

.uploaded-file img {
	width: 60px;
	height: 60px;
	object-fit: cover;
	border-radius: 4px;
	margin-bottom: 5px;
}

.uploaded-file span {
	font-size: 0.8rem;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	max-width: 100px;
}

.remove-file {
	position: absolute;
	top: -8px;
	right: -8px;
	background: #dc3545;
	color: white;
	border: none;
	border-radius: 50%;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 0.7rem;
}

.remove-file:hover {
	background: #c82333;
}

.download-file {
	position: absolute;
	top: -8px;
	left: -8px;
	background: #486ae9;
	color: white;
	border: none;
	border-radius: 50%;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 0.7rem;
}

.download-file:hover {
	background: #1b45e3;
}

.invalid-feedback {
	display: block;
	width: 100%;
	margin-top: 0.25rem;
	font-size: 0.875em;
	color: #dc3545;
}
</style>
